import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Grid,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PostcardTemplateData } from '../../types/postcard';
import { orderApi } from '../../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Address } from '../../types/address';

interface OrderReviewProps {
  postcardId: string;
  postcards: PostcardTemplateData[];
  senderAddress: Address;
  onSuccess: () => void;
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

export const OrderReview: React.FC<OrderReviewProps> = ({
  postcardId,
  postcards,
  senderAddress,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const calculateSubtotal = () => {
    return postcards.length * 4.99; // Base price per postcard
  };

  const calculateShipping = () => {
    return 2.99; // Fixed shipping cost
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping();
  };

  const handlePaymentSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const session = await orderApi.createCheckoutSession({
        postcardId,
        senderAddress: senderAddress,
        recipientData: postcards,
        amount: calculateTotal(),
      });

      // Load Stripe
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe failed to load');

      // Redirect to Stripe Checkout with the first session
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: session.data.sessionId
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during checkout');
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Review Your Order
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Postcards
          </Typography>
          <List>
            {postcards.map((postcard, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar /* todo: custom image src= */ alt={`Postcard ${index + 1}`} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={`Postcard ${index + 1}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          To: {postcard.recipientAddress.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {postcard.recipientAddress.address_line1}
                          {postcard.recipientAddress.address_line2 && `, ${postcard.recipientAddress.address_line2}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {postcard.recipientAddress.address_city}, {postcard.recipientAddress.address_state} {postcard.recipientAddress.address_zip}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Message: {postcard.message}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < postcards.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Subtotal:</Typography>
              <Typography>£{calculateSubtotal().toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Shipping:</Typography>
              <Typography>£{calculateShipping().toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">£{calculateTotal().toFixed(2)}</Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handlePaymentSubmit}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" />
                    Processing...
                  </>
                ) : (
                  'CHECKOUT'
                )}
              </button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}; 